import { graphql } from 'gatsby';

import React from 'react';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import { useHasEnteredViewport, useWindowDimensions } from '../../../utils/hooks';
import { CommonModuleProps } from '../../ModulesContent';
import AnimatedTitle from '../AnimatedTitle';
import Image from '../Image';
import ModuleLayout from '../ModuleLayout';
import ScrollingItems from '../ScrollingItems';
import * as styles from './LogosModule.module.scss';

export const LogosModuleFragment = graphql`
  fragment LogosModule on SanityLogosModule {
    title {
      ...LocaleStringFragment
    }
    logos {
      lightLogo {
        ...SanityImage
      }
      darkLogo {
        ...SanityImage
      }
      websiteUrl
    }
  }
`;

export type LogosModuleProps = {
  title?: string;
  logos: Array<{
    lightLogo: SanityImageType;
    darkLogo: SanityImageType;
    websiteUrl?: string;
  }>;
  className?: string;
};

function LogosModule(props: LogosModuleProps & CommonModuleProps): React.ReactElement {
  const { title, logos, className, moduleId } = props;
  const [viewportRef, hasEnteredViewport] = useHasEnteredViewport();
  const windowDimensions = useWindowDimensions();

  return (
    <ModuleLayout
      id={moduleId}
      ref={viewportRef}
      className={className}
      childrenClassName={styles.contentContainer}
      currentModuleBgColor={props.theme}
      childrenWithoutHorizontalPadding
    >
      {title && (
        <div className={styles.titleContainer}>
          <AnimatedTitle title={title} trigger={styles.contentContainer} className={styles.title} />
        </div>
      )}
      <ScrollingItems
        animationDirection="left"
        animationDuration={
          windowDimensions ? (3 + windowDimensions.width / 400) * logos.length + 1 : 1000
        }
        className={styles.scrollingWrapper}
      >
        <div className={styles.logosContainer}>
          {logos.map((logo, i) => {
            const imageContent = (
              <div className={styles.logoContainer}>
                <Image
                  className={styles.logo}
                  image={
                    props.theme === 'moonlit' || props.theme === 'starlit'
                      ? logo.lightLogo
                      : logo.darkLogo
                  }
                  width={250}
                  eagerLoading={hasEnteredViewport}
                />
              </div>
            );

            const logoAlt =
              logo.lightLogo.asset.altText || logo.darkLogo.asset.altText || '';
            const linkLabel =
              logoAlt ||
              (logo.websiteUrl
                ? `Visit ${new URL(logo.websiteUrl).hostname.replace(/^www\./, '')}`
                : 'Partner');

            return logo.websiteUrl ? (
              <a
                href={logo.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.logoLink}
                key={i}
                aria-label={linkLabel}
              >
                {imageContent}
              </a>
            ) : (
              <span className={styles.logoLink} key={i}>
                {imageContent}
              </span>
            );
          })}
        </div>
      </ScrollingItems>
    </ModuleLayout>
  );
}

export default LogosModule;
